// Helper para animações de grow-from-origin / shrink-to-origin em modais.
// O padrão é o mesmo que o Simulador antigo usa (memória feedback_modal_origin_animation):
// 1. Trigger button chama `captureOriginFromEvent(e)` no onClick.
// 2. O modal renderiza com transform-origin = posição do clique relativa
//    ao próprio modal, e usa keyframe scale(0)→scale(1).
// 3. No fechamento, o modal toca scale(1)→scale(0) com mesma transform-origin,
//    e desmonta no onAnimationEnd.
//
// Implementação minimalista: guardamos o último clique em um signal global,
// modais consomem via `useGrowOrigin()` (hook que retorna ref e aplica
// transform-origin no efeito de montagem).

import { useEffect, useRef, useState } from "preact/hooks";

interface OriginPoint {
  x: number;
  y: number;
}

let _lastOrigin: OriginPoint | null = null;

// Chamar no onClick do trigger (botão que abre o modal). Aceita MouseEvent
// nativo ou objeto com currentTarget. Se receber um elemento, usa o centro
// do elemento como origem (mais estável quando o clique vem do teclado).
export function captureOriginFromEvent(e: unknown): void {
  if (!e || typeof e !== "object") return;
  const ev = e as { clientX?: number; clientY?: number; currentTarget?: unknown };
  // Preferimos as coordenadas do clique se presentes e não zero (cliques de
  // teclado/Enter podem chegar com clientX/Y === 0).
  if (
    typeof ev.clientX === "number" &&
    typeof ev.clientY === "number" &&
    (ev.clientX !== 0 || ev.clientY !== 0)
  ) {
    _lastOrigin = { x: ev.clientX, y: ev.clientY };
    return;
  }
  // Fallback: centro do trigger element
  const el = ev.currentTarget;
  if (el && typeof (el as HTMLElement).getBoundingClientRect === "function") {
    const r = (el as HTMLElement).getBoundingClientRect();
    if (r.width > 0 && r.height > 0) {
      _lastOrigin = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    }
  }
}

// Lê a origem atual. Não reseta — fica até o próximo capture, pra que o
// shrink-to-origin (close) possa reutilizar a mesma posição.
export function readOrigin(): OriginPoint | null {
  return _lastOrigin;
}

// Hook que retorna uma ref pra o elemento raiz do modal. Quando `open`
// muda pra true, aplica transform-origin no elemento baseado no último
// clique capturado. Pode ser reusado quando o modal animar de novo.
export function useGrowOrigin<T extends HTMLElement>(open: boolean) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    if (!open) return;
    const el = ref.current;
    if (!el) return;
    const origin = readOrigin();
    if (!origin) {
      el.style.transformOrigin = "50% 50%";
      return;
    }
    requestAnimationFrame(() => {
      if (!ref.current) return;
      const r = ref.current.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) return;
      const x = ((origin.x - r.left) / r.width) * 100;
      const y = ((origin.y - r.top) / r.height) * 100;
      const cx = Math.max(-20, Math.min(120, x));
      const cy = Math.max(-20, Math.min(120, y));
      ref.current.style.transformOrigin = `${cx}% ${cy}%`;
    });
  }, [open]);
  return ref;
}

// Hook completo: gerencia grow-in (open) + shrink-out (close) com a mesma
// origem. Retorna ref pra a div interna do modal, props pra spread no
// elemento (style + onAnimationEnd), e uma função `requestClose` que o
// modal chama no lugar de fechar direto.
//
// Uso:
//   const { ref, animStyle, onAnimationEnd, requestClose } =
//     useModalGrowShrink(true, () => { openSignal.value = false; });
//   <div ref={ref} style={animStyle} onAnimationEnd={onAnimationEnd}>
export function useModalGrowShrink(open: boolean, finalUnmount: () => void) {
  const ref = useGrowOrigin<HTMLDivElement>(open);
  const [closing, setClosing] = useState(false);

  // Tranca o scroll do body enquanto o modal está montado (aberto ou em
  // animação de fechamento). Sem isso, o conteúdo atrás continua rolando
  // com a roda do mouse / touch — e elementos que extrapolam o backdrop
  // continuam clicáveis. Hook idempotente, seguro com modais empilhados.
  useBodyScrollLock(open);

  // Quando `open` é re-setado pra true do zero (reabertura), garante que
  // o estado "closing" reseta.
  useEffect(() => {
    if (open) setClosing(false);
  }, [open]);

  function requestClose() {
    if (closing) return;
    setClosing(true);
  }

  function onAnimationEnd(e: AnimationEvent) {
    if (e.animationName === "modal-shrink-out") {
      setClosing(false);
      finalUnmount();
    }
  }

  const animStyle = {
    animation: closing
      ? "modal-shrink-out 180ms cubic-bezier(0.4, 0, 1, 1) forwards"
      : "modal-grow-in 220ms cubic-bezier(0.16, 1, 0.3, 1)"
  };

  return { ref, animStyle, onAnimationEnd, requestClose, isClosing: closing };
}

// =========================================================================
// Body scroll lock (e click pass-through) para modais e overlays.
// =========================================================================
// Quando um modal abre, queremos:
//   1) trancar o scroll do body (sem isso o conteúdo atrás continua rolando
//      com a roda do mouse / touch).
//   2) evitar que clicks/touchs vazem para os elementos atrás (cobre o caso
//      em que o backdrop não preenche a viewport inteira por motivos de
//      layout; ao trancar o body também tiramos a possibilidade de interagir
//      com tab/foco em elementos não-modal).
//
// Usamos um contador global porque modais podem empilhar (ex.: bank-chooser
// → modal de PDF). Só destranca quando o último overlay fecha.
let _scrollLockCount = 0;
let _savedBodyOverflow: string | null = null;
let _savedBodyPaddingRight: string | null = null;

function acquireScrollLock() {
  if (_scrollLockCount === 0 && typeof document !== "undefined") {
    const body = document.body;
    _savedBodyOverflow = body.style.overflow;
    _savedBodyPaddingRight = body.style.paddingRight;
    // Compensa a largura da scrollbar pra não dar "salto horizontal" no
    // conteúdo quando trancamos overflow=hidden (some a scrollbar do body).
    const scrollbarW = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarW > 0) body.style.paddingRight = `${scrollbarW}px`;
    body.style.overflow = "hidden";
  }
  _scrollLockCount++;
}

function releaseScrollLock() {
  if (_scrollLockCount === 0) return;
  _scrollLockCount--;
  if (_scrollLockCount === 0 && typeof document !== "undefined") {
    const body = document.body;
    body.style.overflow = _savedBodyOverflow ?? "";
    body.style.paddingRight = _savedBodyPaddingRight ?? "";
    _savedBodyOverflow = null;
    _savedBodyPaddingRight = null;
  }
}

// Hook que tranca/destranca o scroll do body baseado em `active`. Cobre o
// ciclo completo: monta com active=true → acquire; muda pra false → release;
// unmount com active=true → release (cleanup). Idempotente em re-render.
export function useBodyScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    acquireScrollLock();
    return () => releaseScrollLock();
  }, [active]);
}
